/**
 * studio-shop D4 — the product window (`shop-product`): the page's one window
 * for the product a grid opened: its picture, name and description, its
 * formats and sizes with their prices, the shipping line, and Add to bag (the
 * Prints mockup's print window), with Close, and previous and next through the
 * grid. Draws nothing while no product is open. Placed with a product's handle
 * (`product`), it is that product drawn where it is placed instead; an unknown
 * or hidden handle draws nothing and warns once naming it (shop-catalog, "A site
 * places a product by its handle"). The prices are the catalog's; the bag is
 * priced again by the server when it shows. shop-self-fulfilment D5: with none
 * of the picked option left, Add to bag reads Sold out and does nothing, and
 * the state says so for a site's own drawing.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'shop-product',
  description:
    "The page's product window, for the product a grid opened: its picture, name and description, its formats and sizes with prices, the shipping line, and Add to bag. Nothing while none is open.",
  placeable: true,
  options: {
    product: {
      type: 'string',
      default: null,
      maxLength: 64,
      description:
        "A product's handle: shows that product in the page, where it can be picked and added to the bag. Absent: the page's window for the product a grid opens.",
    },
  },
  parts: {
    window: { description: 'The whole product window.', required: false },
    image: { description: "The product's picture.", required: false, role: 'photo' },
    title: { description: "The product's name.", required: true, role: 'title' },
    description: { description: "The studio's words about it.", required: false },
    formats: { description: 'The formats to choose from (paper, canvas), when there is more than one.', required: false },
    format: { description: 'One format; choosing it picks its first size.', required: false, states: ['selected'] },
    sizes: { description: "The chosen format's sizes.", required: false },
    size: { description: 'One size with its price.', required: false, states: ['selected'] },
    price: { description: "The product's price: the picked option's, with its format and size, or its range.", required: true },
    shipping: { description: 'The shipping line: where it ships free; other countries see theirs at checkout.', required: false },
    addToBag: { description: 'Add to bag, with the price; Sold out, doing nothing, when none of the picked option are left.', required: false, role: 'primary-action' },
    full: { description: 'Why no more of it fits in the bag.', required: true },
    close: { description: 'Closes the window.', required: false },
    previous: { description: 'The previous product of the grid.', required: false },
    next: { description: 'The next product of the grid.', required: false },
    position: { description: 'Where the product sits in the grid: "3 of 22".', required: false },
  },
  actions: {
    pickOption: {
      description: 'Picks one option (a format and size) by its id: a format button names its first size, a size button its own.',
      args: [{ name: 'optionId', kind: 'value', type: 'string', maxLength: 64 }],
    },
    addToBag: {
      description: "Puts one of the picked option in the bag, at the price shown, then closes the window and opens the page's bag. Does nothing while it is sold out.",
      args: [],
    },
    close: { description: 'Closes the window.', args: [] },
    previous: { description: 'Shows the previous product of the grid, round the end.', args: [] },
    next: { description: 'Shows the next product of the grid, round the end.', args: [] },
  },
  events: {},
});

/** One format to choose from. */
export interface ShopProductFormat {
  /** The format as the catalog names it ("paper"); null for options with no format. */
  readonly format: string | null;
  /** As drawn: "Paper". */
  readonly label: string;
  /** The option picked when this format is chosen: its first size. */
  readonly optionId: string;
  readonly selected: boolean;
  /** The small copy of the picture this format opens on, to fetch before it is picked; null when there is none. */
  readonly small: string | null;
}

/** One size of the chosen format. */
export interface ShopProductSize {
  readonly optionId: string;
  /** The size ("18 × 24 in"), else the option's label. */
  readonly label: string;
  /** Its price: "$95.00". */
  readonly price: string;
  readonly selected: boolean;
  /** The small copy of this size's picture, to fetch before it is picked; null when there is none. */
  readonly small: string | null;
}

/** What the product window knows. */
export interface ShopProductState {
  /** True while a product is open in the window; always, for a product placed by its handle. */
  readonly open: boolean;
  /** closed (no product is open: nothing is drawn), loading, ready, or missing (the studio no longer shows this product). */
  readonly status: 'closed' | 'loading' | 'ready' | 'missing';
  /** The open product's handle; null while closed. */
  readonly handle: string | null;
  readonly title: string;
  readonly description: string | null;
  /** The picked size's picture with the size it is drawn in the shape of ("18 × 24 in"), else the product's first, drawn whole (size null). */
  readonly image: {
    readonly url: string;
    /** A small copy of the same picture, about 480 px wide, to show while `url` loads; null when the shop has none yet. */
    readonly small: string | null;
    readonly alt: string;
    readonly size: string | null;
  } | null;
  /** The formats on sale; one entry when the product has a single format. */
  readonly formats: readonly ShopProductFormat[];
  /** The chosen format's sizes. */
  readonly sizes: readonly ShopProductSize[];
  /** The picked option's id, label ("Paper 18 × 24 in") and price ("$95.00"); null while loading. */
  readonly picked: { readonly optionId: string; readonly label: string; readonly price: string } | null;
  /** None of the picked option are left: Add to bag reads Sold out and does nothing (shop-self-fulfilment D5). */
  readonly soldOut: boolean;
  /** The product's lowest price of all ("$60.00"); null until it is ready. */
  readonly from: string | null;
  /** Its highest price of all ("$350.00"); the same as `from` when every option costs the same. */
  readonly to: string | null;
  /** The shipping line: "Free shipping in the United States. Other countries see their shipping at checkout.", or "Shipping and tax are worked out at checkout." */
  readonly shipping: string;
  /** idle, added (it just went in the bag), or full (no more of it fits). */
  readonly adding: 'idle' | 'added' | 'full';
  /** Why no more fits, while `adding` is full. */
  readonly full: string | null;
  /** Where the product sits in the grid that opened it: "3 of 22"; null when that grid holds only it, or for a product placed by its handle. */
  readonly position: string | null;
}

/** What a drawing can ask the product window to do. */
export interface ShopProductActions {
  /** Picks an option by its id. */
  readonly pickOption: (optionId: string) => void;
  /** Puts one of the picked option in the bag. */
  readonly addToBag: () => void;
  /** Closes the window. */
  readonly close: () => void;
  /** Shows the previous product of the grid. */
  readonly previous: () => void;
  /** Shows the next product of the grid. */
  readonly next: () => void;
}

/** The product window reports nothing to its host. */
export type ShopProductEvents = Record<string, never>;
